import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Snackbar, Tooltip } from '@mui/material';
import { IconCheck, IconEdit, IconRefresh } from '@tabler/icons-react';
import type { M365Tenant } from '@techgitter/contracts';
import {
  clearM365TenantMapping,
  confirmM365TenantMapping,
  fetchM365Tenants,
  mapM365Tenant,
} from '../services/m365TenantsApi';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarSelect,
  ToolbarSpacer,
  ToolbarCount,
} from '../components/toolbar';
import { M365TenantMappingPanel } from '../components/admin/M365TenantMappingPanel';
import {
  MAP_SOURCE_LABELS,
  MAP_SOURCE_TONES,
  OUTCOME_LABELS,
  OUTCOME_TONES,
  formatDate,
  matchesSearch,
  needsAttention,
} from '../utils/m365TenantDisplay';
import '../components/admin/M365TenantMappingPanel.css';

/** The first option is a *view*, not a single map source — it spans the two
 *  tiers worth reviewing. See `needsAttention`. */
const ATTENTION = 'attention';

const MAP_SOURCE_OPTIONS = [
  { value: ATTENTION, label: 'Needs attention' },
  { value: '', label: 'All tenants' },
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'email_domain', label: 'Email domain' },
  { value: 'managed_domain', label: 'Managed domain' },
  { value: 'native_domain', label: 'Native domain' },
  { value: 'manual', label: 'Manual' },
];

const OUTCOME_OPTIONS = [
  { value: '', label: 'Any outcome' },
  { value: 'success', label: 'Success' },
  { value: 'partial', label: 'Partial' },
  { value: 'failed', label: 'Failed' },
  { value: 'unmapped', label: 'Unmapped' },
  { value: 'skipped', label: 'Skipped' },
];

type Snack = { message: string; severity: 'success' | 'error' };

/**
 * Admin "Microsoft 365" — the customer ↔ M365 tenant map (requirements §11.5).
 *
 * The FR2 reconciler in sm4rt-cipp-integration auto-matches tenants to customers
 * on three tiered domain signals and refuses to guess when they are ambiguous or
 * absent. This page is where the rest get decided. Before it existed, the only
 * way to correct a mapping was the Dataverse maker portal.
 *
 * Every action here sets `mapConfirmed`, which is what makes the decision survive
 * the nightly run — the reconciler re-decides anything unconfirmed.
 *
 * The whole map is ~131 rows, so it is fetched once and filtered in the browser:
 * search stays instant and switching filters costs no round trip. The server-side
 * filters exist on `/api/m365/tenants` for other consumers.
 */
export const M365TenantMapAdminPage: React.FC = () => {
  const [tenants, setTenants] = useState<M365Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mapSource, setMapSource] = useState<string>(ATTENTION);
  const [outcome, setOutcome] = useState<string>('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<M365Tenant | null>(null);
  const [busyTenantId, setBusyTenantId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<Snack | null>(null);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setLoadError(null);
      setTenants(await fetchM365Tenants({}, signal));
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return;
      setLoadError(e instanceof Error ? e.message : 'Failed to load the M365 tenant map');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const visible = useMemo(
    () =>
      tenants.filter((t) => {
        if (mapSource === ATTENTION && !needsAttention(t)) return false;
        if (mapSource && mapSource !== ATTENTION && t.mapSource !== mapSource) return false;
        if (outcome && t.outcome !== outcome) return false;
        return matchesSearch(t, search);
      }),
    [tenants, mapSource, outcome, search],
  );

  const attentionCount = useMemo(() => tenants.filter(needsAttention).length, [tenants]);

  /** Every mutation funnels through here so the busy state, the toast and the
   *  reload are identical whichever action ran. */
  const runAction = useCallback(
    async (tenant: M365Tenant, action: () => Promise<unknown>, done: string) => {
      setBusyTenantId(tenant.tenantId);
      try {
        await action();
        setSnackbar({ message: done, severity: 'success' });
        setEditing(null);
        await refresh();
      } catch (e) {
        setSnackbar({
          message: e instanceof Error ? e.message : 'The mapping could not be saved',
          severity: 'error',
        });
      } finally {
        setBusyTenantId(null);
      }
    },
    [refresh],
  );

  const handleSave = (tenant: M365Tenant) => (customerId: string) =>
    runAction(
      tenant,
      () => mapM365Tenant(tenant.tenantId, customerId),
      `${tenant.displayName || 'Tenant'} mapped and confirmed`,
    );

  const handleClear = (tenant: M365Tenant) => () =>
    runAction(
      tenant,
      () => clearM365TenantMapping(tenant.tenantId),
      `${tenant.displayName || 'Tenant'} unmapped — the nightly reconcile will leave it alone`,
    );

  const handleConfirm = (tenant: M365Tenant) =>
    runAction(
      tenant,
      () => confirmM365TenantMapping(tenant.tenantId),
      `${tenant.displayName || 'Tenant'} mapping confirmed`,
    );

  const panelOpen = editing !== null;

  return (
    <Box
      sx={{
        background: '#f0f4f9',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Matched via"
          value={mapSource}
          onChange={setMapSource}
          options={MAP_SOURCE_OPTIONS}
        />
        <ToolbarSelect
          label="Last sync"
          value={outcome}
          onChange={setOutcome}
          options={OUTCOME_OPTIONS}
        />
        <ToolbarSearch
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search tenant, domain or customer…"
        />
        <ToolbarSpacer />
        <ToolbarCount>
          {`${visible.length} of ${tenants.length} · ${attentionCount} need${
            attentionCount === 1 ? 's' : ''
          } attention`}
        </ToolbarCount>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => refresh()} disabled={loading}>
              <IconRefresh size={18} stroke={1.5} />
            </IconButton>
          </span>
        </Tooltip>
      </SecondaryToolbar>

      {/* Two-column layout — table left, mapping panel right. Collapses to a
          single column when the panel is closed. */}
      <div
        className={`m365-tenants-layout ${
          panelOpen ? 'm365-tenants-layout--panel-open' : 'm365-tenants-layout--panel-closed'
        }`}
      >
        <Box sx={{ p: '0 16px 24px', minWidth: 0 }}>
          {loadError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {loadError}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataTableCard>
              <DataTable>
                <thead>
                  <tr>
                    <DataTableHeadCell>Tenant</DataTableHeadCell>
                    <DataTableHeadCell>Domains</DataTableHeadCell>
                    <DataTableHeadCell>Customer</DataTableHeadCell>
                    <DataTableHeadCell>Matched via</DataTableHeadCell>
                    <DataTableHeadCell>Confirmed</DataTableHeadCell>
                    <DataTableHeadCell>Last sync</DataTableHeadCell>
                    <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <DataTableEmpty colSpan={7}>
                      {mapSource === ATTENTION
                        ? 'Nothing needs attention — every tenant is mapped or confirmed'
                        : 'No tenants match these filters'}
                    </DataTableEmpty>
                  ) : (
                    visible.map((t) => (
                      <DataTableRow key={t.tenantId}>
                        <DataTableCell>
                          <div style={{ fontWeight: 600 }}>{t.displayName || t.tenantId}</div>
                          {t.nativeDomain && (
                            <div style={{ color: '#646464', fontSize: 12 }}>{t.nativeDomain}</div>
                          )}
                        </DataTableCell>
                        <DataTableCell muted>
                          {/* The domain is the evidence a mapping rests on, so the
                              primary one is shown rather than hidden behind a count. */}
                          {t.defaultDomain || t.domains?.[0] || '—'}
                          {t.domains && t.domains.length > 1 && (
                            <span
                              style={{ color: '#aaa', fontSize: 11, marginLeft: 6 }}
                              title={t.domains.join(', ')}
                            >
                              {`+${t.domains.length - 1}`}
                            </span>
                          )}
                        </DataTableCell>
                        <DataTableCell>
                          {t.customerName || (
                            <span style={{ color: '#ccc', fontStyle: 'italic' }}>Not mapped</span>
                          )}
                        </DataTableCell>
                        <DataTableCell>
                          <Chip tone={MAP_SOURCE_TONES[t.mapSource] ?? 'neutral'}>
                            {MAP_SOURCE_LABELS[t.mapSource] ?? t.mapSource}
                          </Chip>
                          {t.matchedOn && (
                            <div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>
                              {t.matchedOn}
                            </div>
                          )}
                        </DataTableCell>
                        <DataTableCell>
                          {t.mapConfirmed ? (
                            <Chip tone="success" title={`Confirmed ${formatDate(t.mappedAt)}`}>
                              Confirmed
                            </Chip>
                          ) : (
                            <span style={{ color: '#ccc' }}>—</span>
                          )}
                        </DataTableCell>
                        <DataTableCell>
                          <Chip tone={OUTCOME_TONES[t.outcome] ?? 'neutral'}>
                            {OUTCOME_LABELS[t.outcome] ?? t.outcome}
                          </Chip>
                          <div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>
                            {t.outcome === 'success'
                              ? `${t.usersSynced} user(s) · ${formatDate(t.lastSuccessAt)}`
                              : formatDate(t.lastSyncedAt)}
                          </div>
                        </DataTableCell>
                        <DataTableCell align="right">
                          {/* Confirming an existing auto-match is the cheap,
                              common action, so it lives in the row. Changing one
                              needs the customer picker, so it opens the panel. */}
                          {t.customerId && !t.mapConfirmed && (
                            <Tooltip title="Confirm this mapping — locks it against the nightly reconcile">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={busyTenantId === t.tenantId}
                                  onClick={() => handleConfirm(t)}
                                >
                                  <IconCheck size={18} stroke={1.5} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={t.customerId ? 'Change mapping' : 'Map to a customer'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={busyTenantId === t.tenantId}
                                onClick={() => setEditing(t)}
                              >
                                <IconEdit size={18} stroke={1.5} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </DataTableCell>
                      </DataTableRow>
                    ))
                  )}
                </tbody>
              </DataTable>
            </DataTableCard>
          )}
        </Box>

        {editing && (
          <div className="m365-tenant-panel-col">
            <M365TenantMappingPanel
              key={editing.tenantId}
              tenant={editing}
              busy={busyTenantId === editing.tenantId}
              onClose={() => setEditing(null)}
              onSave={handleSave(editing)}
              onClear={handleClear(editing)}
            />
          </div>
        )}
      </div>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity ?? 'success'} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
